import React, { useState, useContext, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import { EnvelopeFill, LockFill } from 'react-bootstrap-icons';
import { AppContext } from '../window';
import { AuthorizedUser } from '../models/User';
import Loader from '../libs/Loader';
import { NotificationContext } from '../context/NotificationContext';
import { useTranslation } from '../i18n';
import { updateCsrfMetaToken } from '../api/fetch';
import RequestResetModal from './RequestResetModal';

interface LoginModalProps {
  show: boolean;
  onHide: () => void;
  onLoginSuccess?: () => void;
  onShowSignup?: () => void;
  /** Gets the typed email, so the reset modal can prefill it */
  onShowPasswordReset?: (email: string) => void;
  /** Prefills the email field each time the modal opens */
  initialEmail?: string;
  /** Why the modal opened, shown above the form (e.g. the session was lost) */
  notice?: string | undefined;
}

const LoginModal: React.FC<LoginModalProps> = ({ show, onHide, onLoginSuccess, onShowSignup, onShowPasswordReset, initialEmail, notice }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail ?? '');
  useEffect(() => {
    if (show && initialEmail) {
      setEmail(initialEmail);
    }
  }, [show, initialEmail]);
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const appContext = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  // Without an onShowPasswordReset (home's `?login=1`, the signup forms) the
  // login modal opens the reset-request modal itself, hiding while it is open,
  // so "back to login" can show it again
  const [showReset, setShowReset] = useState(false);
  const [closeAfterReset, setCloseAfterReset] = useState(false);
  useEffect(() => {
    if (!showReset && closeAfterReset) {
      setCloseAfterReset(false);
      onHide();
    }
  }, [showReset, closeAfterReset, onHide]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      // Make fetch request to login API using form-urlencoded
      const formData = new URLSearchParams();
      formData.append('LoginForm[email]', email);
      formData.append('LoginForm[password]', password);

      const response = await fetch('/user/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        updateCsrfMetaToken(data.csrfToken);
        // Clear form
        // setEmail('');
        setPassword('');
        appContext.setUser(data.user as AuthorizedUser)
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        onHide();
      } else {
        setErrorMessage(data.error || t('auth.login_failed'));
      }
    } catch (error) {
      setErrorMessage(t('auth.error_occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal show={show && !showReset} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('auth.login')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {notice && !errorMessage && (
          <Alert variant="warning" className="login-notice">{notice}</Alert>
        )}
        {errorMessage && (
          <Alert variant="danger">{errorMessage}</Alert>
        )}

        {isSubmitting && (
          <Loader />
        )}

        <Form onSubmit={handleSubmit} className={isSubmitting ? 'd-none' : ''}>
          <Form.Group className="mb-3" controlId="formEmail">
            <Form.Label>{t('auth.email_address')}</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <EnvelopeFill size={16} />
              </InputGroup.Text>
              <Form.Control 
                type="email" 
                placeholder={t('auth.enter_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPassword">
            <Form.Label>{t('auth.password')}</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <LockFill size={16} />
              </InputGroup.Text>
              <Form.Control 
                type="password" 
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </InputGroup>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button 
              variant="link" 
              className="p-0 text-decoration-none" 
              onClick={() => {
                if (onShowPasswordReset) {
                  onHide();
                  onShowPasswordReset(email);
                } else {
                  setShowReset(true);
                }
              }}
              style={{
                fontSize: '0.9rem',
                fontWeight: 500,
                background: 'linear-gradient(120deg, #007bff, #0056b3)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              {t('auth.forgot_password')}
            </Button>
          </div>
          
          <Button 
            variant="primary" 
            type="submit" 
            className="w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('auth.logging_in') : t('auth.login')}
          </Button>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100 text-center">
          {t('auth.no_account')}{' '}
          <Button 
            variant="link" 
            className="p-0" 
            onClick={() => {
              onHide();
              if (onShowSignup) {
                onShowSignup();
              }
            }}
          >
            {t('auth.register_now')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    {!onShowPasswordReset && (
      <RequestResetModal
        show={show && showReset}
        onHide={() => { setShowReset(false); setCloseAfterReset(true); }}
        onShowLogin={() => setCloseAfterReset(false)}
        initialEmail={email || undefined}
      />
    )}
    </>
  );
};

export default LoginModal;
